import { assertCanModifyDatasetFolder, type UserRole } from "@/app/userRole";
import { apiClient } from "@/http";
import { apiRoutes } from "./apiRoutes";
import type { DatasetStorageBackend } from "./datasetStorageBackend";

// 사람이 카탈로그를 정리하는 폴더 축이다. backend `docs/specs/dataset-folders.md` 가 계약을 소유한다.
//   - organization 전체가 같은 트리를 본다.
//   - 데이터셋 하나는 폴더 하나에 속한다(미분류 = null).
//   - 트리는 **최대 2계층**이다.
//
// `datasetDirectoryTree.ts` 의 `DirectoryFolder` 와 **다른 개념**이다. `DirectoryFolder` 는
// object storage key 와 같은 구조의 데이터셋 안 물리/의미 디렉터리다(`action/`,
// `observation_image/<camera>/`). 그래서 경로가 곧 정체성이다. 이 폴더는 사람이 만든 분류 축이다.
// 이름을 바꿔도 소속이 유지된다. 두 트리를 한 타입으로 합치지 않는다.

// 쓰기 권한 인자다. `dataset.ts` 의 `DatasetMutationOptions` 와 모양이 같지만 그 파일에서
// import 하지 않는다. `dataset.ts` 가 아래 `DatasetFolderRef` 를 쓰므로, 서로 import 하면
// 모듈 순환이 생긴다. backend 가 `service/dataset_folder/ref.py` 를 leaf 로 둔 것과 같은 이유다.
export type DatasetFolderMutationOptions = { role: UserRole | null };

// 데이터셋이 속한 폴더 참조다. 목록 행은 이 값으로 "Picking / 2026-Q1" 을 추가 요청 없이 표시한다.
// 트리가 2계층이라서 `parent` 중첩 하나로 경로가 완성된다. `parent` 가 null 이면 1계층 루트다.
export type DatasetFolderRef = {
  uuid: string;
  display_name: string;
  parent: { uuid: string; display_name: string } | null;
};

// 트리의 한 노드다. **`dataset_count` 는 자손을 포함한다.** 부모를 고른 `?folder=` 질의는
// 자손까지 포함한다. 이 수가 그 질의의 `total` 과 같아야 화면의 두 값이 일치한다.
// 깊이가 2 라서 `children` 안의 노드는 항상 빈 `children` 을 가진다.
export type DatasetFolderNode = {
  uuid: string;
  display_name: string;
  dataset_count: number;
  children: readonly DatasetFolderNode[];
};

// `GET /v1/datasets/folders` 응답이다. 서버가 형제 노드를 이름순으로 정렬한다.
//
// 합계 불변식: **루트 노드들의 `dataset_count` 합 + `unfiled_count` = 목록 기본 스코프의
// `total`**. 자식의 수는 부모에 이미 들어 있다. 건수는 목록 기본 스코프(available·반입 seed
// 제외)로 센다. 그래서 보관됨 탭처럼 다른 스코프를 보는 목록과는 비교하지 않는다.
//
// 저장 위치 게이트를 준 트리는 그 저장 위치의 Dataset 만 센다. 위 불변식의 `total` 은 같은 게이트를
// 준 목록의 `total` 이다. 폴더 목록은 게이트와 무관하다. 건수가 0 인 폴더도 트리에 있다.
export type DatasetFolderTree = {
  folders: readonly DatasetFolderNode[];
  unfiled_count: number;
};

// 생성 / 수정 응답이다. 폴더 한 개를 담는다.
export type DatasetFolderResp = {
  uuid: string;
  display_name: string;
  parent_uuid: string | null;
};

// `?folder=` 와 일괄 이동에서 미분류를 고르는 토큰이다. 다른 축의 `unknown`("판정하지 못했다")과
// 뜻이 달라서 다른 이름을 쓴다. 폴더가 없다는 것은 사람이 아직 폴더에 넣지 않았다는 뜻이다.
export const unfiledFolderToken = "unfiled";

// 한 요청으로 옮길 수 있는 Dataset 수다. backend `DATASET_BULK_MOVE_MAX` 와 같은 값이다.
// 일괄 보관과 상한이 같아서 버튼 잠금도 같은 방식으로 적용한다.
export const datasetBulkMoveLimit = 100;

// 폴더 트리가 받는 게이트다. 목록의 같은 게이트와 값이 같아야 건수가 목록 total 과 맞는다.
export type DatasetFolderTreeGates = {
  // 목록의 `?storage_backend=` 와 같은 값이다. null 이면 모든 저장 위치의 Dataset 을 센다.
  storageBackend: DatasetStorageBackend | null;
};

// organization 의 폴더 트리, 폴더별 건수, 미분류 건수를 조회한다. 조직 멤버면 누구나 읽는다.
export async function getDatasetFolderTree(
  gates: DatasetFolderTreeGates,
): Promise<DatasetFolderTree> {
  const resp = await apiClient.get<DatasetFolderTree>(apiRoutes.dataset.folders, {
    params: gates.storageBackend === null ? {} : { storage_backend: gates.storageBackend },
  });
  return resp.data;
}

// 폴더를 생성한다(조직 멤버). `parent_uuid` 가 null 이면 1계층 루트다.
// 422 folder.depth_exceeded(부모가 이미 2계층) / 409 folder.duplicate_name.
export async function createDatasetFolder(
  payload: { display_name: string; parent_uuid: string | null },
  options: DatasetFolderMutationOptions,
): Promise<DatasetFolderResp> {
  assertCanModifyDatasetFolder(options.role);
  const resp = await apiClient.post<DatasetFolderResp>(apiRoutes.dataset.folders, payload);
  return resp.data;
}

// 폴더 이름을 바꾸고 폴더를 이동한다(조직 멤버).
//
// 서버가 `exclude_unset` 으로 읽으므로 **바꿀 키만 보낸다**. 명시적 `parent_uuid: null` 은
// "루트로 꺼내기" 다. 키를 생략하면 "부모 유지" 다. 이름만 바꾸면서 부모 키를 함께 보내면
// 의도하지 않은 이동이 일어난다.
export async function updateDatasetFolder(
  folderUuid: string,
  payload: { display_name?: string; parent_uuid?: string | null },
  options: DatasetFolderMutationOptions,
): Promise<DatasetFolderResp> {
  assertCanModifyDatasetFolder(options.role);
  const resp = await apiClient.patch<DatasetFolderResp>(
    apiRoutes.dataset.folder(folderUuid),
    payload,
  );
  return resp.data;
}

// 폴더를 삭제한다(조직 멤버). **서버는 서브트리를 해체한다.** 자식 폴더도 함께 사라진다.
// 1계층과 2계층에 있던 Dataset 은 모두 미분류가 된다. 데이터는 지워지지 않는다.
export async function deleteDatasetFolder(
  folderUuid: string,
  options: DatasetFolderMutationOptions,
): Promise<void> {
  assertCanModifyDatasetFolder(options.role);
  await apiClient.delete(apiRoutes.dataset.folder(folderUuid));
}

// 고른 데이터셋을 한 폴더로 옮긴다(조직 멤버). `folder_uuid` 가 null 이면 미분류로 꺼낸다.
//
// `applied` 는 중복을 제거한 대상 수다. 이미 그 폴더에 있던 데이터셋도 요청한 상태가 되었으므로
// 센다. 건너뛰는 경우가 없어서 `skipped` 는 없다. 스코프 밖 데이터셋이 하나라도 있으면 서버는
// 전체를 404 로 거절한다.
export async function moveDatasetsToFolder(
  payload: { dataset_uuids: readonly string[]; folder_uuid: string | null },
  options: DatasetFolderMutationOptions,
): Promise<{ applied: number }> {
  assertCanModifyDatasetFolder(options.role);
  const resp = await apiClient.post<{ applied: number }>(apiRoutes.dataset.bulkMove, payload);
  return resp.data;
}
